import { useState, useEffect } from "react";
import { cn, getOptimizedImageUrl } from "@/lib/utils";

interface ServiceImageSlideshowProps {
  images: string[];
  alt: string;
  className?: string;
  interval?: number;
}

export function ServiceImageSlideshow({
  images,
  alt,
  className,
  interval = 3000
}: ServiceImageSlideshowProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, interval);

    return () => clearInterval(timer);
  }, [images.length, interval]);

  if (images.length === 0) {
    return (
      <div className={cn("bg-muted", className)}>
        <img
          src="https://images.unsplash.com/photo-1606216794074-735e91aa2c92?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  if (images.length === 1) {
    return (
      <div className={className}>
        <img
          src={getOptimizedImageUrl(images[0], { width: 600, height: 400, resize: 'cover' })}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {images.map((image, index) => (
        <img
          key={index}
          src={getOptimizedImageUrl(image, { width: 600, height: 400, resize: 'cover' })}
          alt={`${alt} ${index + 1}`}
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity duration-1000",
            index === currentIndex ? "opacity-100" : "opacity-0"
          )}
          loading={index === 0 ? "eager" : "lazy"}
        />
      ))}

      {/* Indicator dots */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 bg-black/50 px-2 py-1 rounded-full">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={cn(
              "w-1.5 h-1.5 rounded-full transition-all duration-300",
              index === currentIndex
                ? "bg-white w-4"
                : "bg-white/50 hover:bg-white/75"
            )}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
